import { UUID } from '@/types/types';
import {
	CreatorProductModuleSerializeData,
	ProductModule,
	SerializedProductModule,
} from '../objects/ProductModule';
import { ProjectRenderer } from '../ProjectRenderer';
import {
	LandscapeModule,
	SerializedLandscapeModule,
} from '../objects/LandscapeModule';
import { CollidableObject } from '../objects/CollidableObject';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { RotationDirection, ThreeEvent } from '../types';
import APP_CONFIG from '@/configs/appConfig';

export type SerializedModulesManager = {
	productModules: SerializedProductModule[];
	landscapeModules: SerializedLandscapeModule[];
};

export class ModulesManager {
	private _areCodesVisible: boolean = APP_CONFIG.DEFAULT_CODES_VISIBLITY;
	productModules: Map<UUID, ProductModule> = new Map();
	landscapeModules: Map<UUID, LandscapeModule> = new Map();

	constructor(private renderer: ProjectRenderer) {}

	get areCodesVisible() {
		return this._areCodesVisible;
	}

	set areCodesVisible(visible: boolean) {
		this._areCodesVisible = visible;
		this.setCSS2DObjectsVisibility(visible);
	}

	private setCSS2DObjectsVisibility(visible: boolean) {
		this.productModules.forEach((productModule) => {
			productModule.css2dObject.visible = visible;
		});
	}

	private removeCollidedModules(id: UUID) {
		this.productModules.forEach((mod) => {
			mod.removeCollidedModules(id);
		});
		this.landscapeModules.forEach((mod) => {
			mod.removeCollidedModules(id);
		});
	}

	rotateModule(id: UUID, direction: RotationDirection) {
		const rendererModule = this.getCollidableModule(id);
		if (!rendererModule) return;

		rendererModule.rotate(direction);
	}

	async addProductModule(
		creatorProductModuleData: CreatorProductModuleSerializeData,
		omitCollisionCheck = false
	) {
		if (!creatorProductModuleData.asset?.model)
			throw new Error('PRODUCT_MODULE_MISSING_ASSET');

		const productModule = new ProductModule(
			this.renderer,
			creatorProductModuleData
		);

		await productModule.loadAsset(creatorProductModuleData.asset.model.path);

		if (!omitCollisionCheck)
			this.renderer.transformController.collisionHandler.checkCollisions(
				productModule
			);

		this.productModules.set(productModule.id, productModule);

		return productModule;
	}

	removeLandscapeModule(id: UUID) {
		const landscapeModule = this.landscapeModules.get(id);
		if (!landscapeModule) return;

		if (landscapeModule.selected) landscapeModule.selected = false;

		landscapeModule.dispose();
		this.landscapeModules.delete(id);

		this.removeCollidedModules(id);
	}

	async addLandscapeModule(creatorLandscapeModule: CreatorLandscapeModule) {
		if (!creatorLandscapeModule.landscapeModule.asset?.model)
			throw new Error('PRODUCT_MODULE_MISSING_ASSET');

		const landscapeModule = new LandscapeModule(
			this.renderer,
			creatorLandscapeModule
		);

		await landscapeModule.loadAsset(
			creatorLandscapeModule.landscapeModule.asset.model.path
		);

		this.landscapeModules.set(landscapeModule.id, landscapeModule);

		return landscapeModule;
	}

	removeProductModule(id: UUID) {
		const productModule = this.productModules.get(id);
		if (!productModule) return;

		if (productModule.selected) productModule.selected = false;

		productModule.dispose();
		this.productModules.delete(id);

		this.removeCollidedModules(id);
	}

	getCollidableModule(id: UUID) {
		const productModule = this.productModules.get(id),
			landscapeModule = this.landscapeModules.get(id);

		return productModule || landscapeModule;
	}

	getCollidableModules(): CollidableObject[] {
		return [...this.landscapeModules.values(), ...this.productModules.values()];
	}

	deselectAll(forceModule?: CollidableObject) {
		this.productModules.forEach((productModule) => {
			if (productModule === forceModule) return;
			productModule.selected = false;
		});
		this.landscapeModules.forEach((landscapeModule) => {
			if (landscapeModule === forceModule) return;
			landscapeModule.selected = false;
		});

		this.renderer.emitter.emit(
			ThreeEvent.SELECT_MODULE,
			forceModule?.id || null
		);
	}

	setShapesVisibility(visible: boolean) {
		this.productModules.forEach((productModule) => {
			productModule.collisionZoneMesh.visible = visible;
		});
		this.landscapeModules.forEach((productModule) => {
			productModule.collisionZoneMesh.visible = visible;
		});
	}

	setAvailability() {
		this.deselectAll();
	}

	export(): SerializedModulesManager {
		return {
			productModules: Array.from(
				this.productModules
					.values()
					.map((productModule) => productModule.export())
			),
			landscapeModules: Array.from(
				this.landscapeModules
					.values()
					.map((landscapeModule) => landscapeModule.export())
			),
		};
	}

	async loadFromData(data: SerializedModulesManager) {
		for (const config of data.productModules) {
			const prodMod = await this.addProductModule(
				config.creatorProductModuleData,
				true
			);
			prodMod.loadFromData(config);
		}
		for (const config of data.landscapeModules) {
			const landMod = await this.addLandscapeModule(
				config.creatorLandscapeModule
			);
			landMod.loadFromData(config);
		}
	}
}
